import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/apiService";
import { SiteFooter, SiteHeader } from "./SiteChrome";
import { useCmsBatch } from "../hooks/useCmsBatch";

const ageGroupLabels = { "1-3": "Ages 1-3", "4-5": "Ages 4-5", "6-8": "Ages 6-8", "9-11": "Ages 9-11", "12-19": "Ages 12-19", adult: "Adults" };

function ProgramTile({ program }) {
  return (
    <article className="program-tile">
      <span className="program-tile-age">{ageGroupLabels[program.ageGroup] || program.ageGroup}</span>
      <h3>{program.name}</h3>
      <p>{program.description || "Details coming soon."}</p>
      <div className="program-tile-meta">
        <span><strong>{program.sessionDurationMinutes}</strong> min</span>
        <span>Ratio <strong>{program.coachPlayerRatio}</strong></span>
        {program.priceCents > 0 && <span><strong>${(program.priceCents / 100).toFixed(0)}</strong></span>}
      </div>
      <Link className="button button--lime" to={`/programs/${program.slug}`}>View program</Link>
    </article>
  );
}

function JoinProgram({ programId }) {
  const { accessToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      api.get("/players").then((response) => setPlayers(response.data.players || []));
    }
  }, [isAuthenticated, accessToken]);

  if (!isAuthenticated) {
    return (
      <div className="join-panel">
        <p className="field-label" style={{ marginBottom: 16 }}>Ready to join?</p>
        <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          Sign in to your account to submit interest in this program.
        </p>
        <Link className="button button--lime" to="/login">Login to join</Link>
      </div>
    );
  }

  const join = async () => {
    setError("");
    setMessage("");
    if (!selected) return setError("Select a player first");
    setSubmitting(true);
    try {
      await api.post(`/programs/${programId}/join`, { playerId: selected });
      setMessage("Interest submitted. Our team will contact you shortly.");
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || "Unable to join program");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="join-panel">
      <p className="field-label" style={{ marginBottom: 16 }}>Join this program</p>
      {players.length ? (
        <>
          <div className="join-panel-field">
            <label className="field-label" htmlFor="join-player">Choose a player</label>
            <select id="join-player" className="field-select" value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value="">Select player…</option>
              {players.map((p) => <option key={p._id} value={p._id}>{p.firstName} {p.lastName}</option>)}
            </select>
          </div>
          <button className="button button--lime" onClick={join} disabled={submitting}>
            {submitting ? "Submitting…" : "Join now"}
          </button>
        </>
      ) : (
        <button className="button button--lime" onClick={() => navigate("/dashboard")}>Add a player first</button>
      )}
      {error && <p role="alert" style={{ color: "#ff6b6b", marginTop: 14, fontSize: 13 }}>{error}</p>}
      {message && <p role="status" style={{ color: "#0a7a2f", marginTop: 14, fontSize: 13 }}>{message}</p>}
    </div>
  );
}

export function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [error, setError] = useState("");
  const [ageGroup, setAgeGroup] = useState("all");
  const { content } = useCmsBatch(["programs-hero"]);
  const hero = content["programs-hero"] || {};

  useEffect(() => {
    api.get("/programs")
      .then((response) => setPrograms(response.data.programs || []))
      .catch((e) => setError(e.message));
  }, []);

  const ageGroups = useMemo(() => [...new Set(programs.map((p) => p.ageGroup))], [programs]);
  const filtered = ageGroup === "all" ? programs : programs.filter((p) => p.ageGroup === ageGroup);

  return (
    <>
      <SiteHeader />
      <main className="site-shell" style={{ paddingTop: 120, minHeight: "70vh", paddingBottom: 100 }}>
        <p className="eyebrow"><span>03</span>Programs</p>
        <h1 className="page-title">{hero.title || "Train with purpose."}</h1>
        <p className="page-intro">{hero.body || "Age-specific development built around measurable progress, expert coaching, and a clear path forward."}</p>

        {error && <p role="alert" style={{ marginTop: 24, color: "#ff6b6b" }}>{error}</p>}

        {!error && ageGroups.length > 1 && (
          <div className="program-filters">
            <button className={`program-filter-chip ${ageGroup === "all" ? "is-active" : ""}`} onClick={() => setAgeGroup("all")}>All ages</button>
            {ageGroups.map((group) => (
              <button key={group} className={`program-filter-chip ${ageGroup === group ? "is-active" : ""}`} onClick={() => setAgeGroup(group)}>
                {ageGroupLabels[group] || group}
              </button>
            ))}
          </div>
        )}

        {!error && !programs.length && <p style={{ marginTop: 40, color: "var(--muted)" }}>Programs are being prepared. Check back soon.</p>}

        {!error && programs.length > 0 && (
          <div className="programs-grid">
            {filtered.map((program) => <ProgramTile key={program._id} program={program} />)}
          </div>
        )}
      </main>
      <SiteFooter />
    </>
  );
}

export function ProgramDetailPage() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/programs/${slug}`)
      .then((response) => setProgram(response.data.program))
      .catch((e) => setError(e.message));
  }, [slug]);

  if (error) {
    return (
      <>
        <SiteHeader />
        <main className="site-shell" style={{ paddingTop: 140, minHeight: "70vh" }}>
          <p role="alert">{error}</p>
          <Link to="/programs" style={{ color: "var(--ink)", fontWeight: 600 }}>Back to programs</Link>
        </main>
        <SiteFooter />
      </>
    );
  }
  if (!program) {
    return (
      <>
        <SiteHeader />
        <main className="site-shell" style={{ paddingTop: 140, minHeight: "70vh" }}>Loading program…</main>
        <SiteFooter />
      </>
    );
  }

  const columns = [
    ["Activities", program.activities],
    ["Technology", program.technology],
    ["Outcomes", program.outcomes],
  ];

  return (
    <>
      <SiteHeader />
      <main className="site-shell" style={{ paddingTop: 120, minHeight: "70vh", paddingBottom: 100 }}>
        <Link to="/programs" style={{ color: "var(--ink)", fontWeight: 600, fontSize: 13 }}>← All programs</Link>

        <p className="eyebrow" style={{ marginTop: 42 }}><span>{ageGroupLabels[program.ageGroup] || program.ageGroup}</span>Age group</p>
        <h1 className="page-title">{program.name}</h1>
        <p className="page-intro" style={{ maxWidth: 700 }}>{program.description}</p>

        <div className="program-detail-stats">
          <div><small>Session</small><strong>{program.sessionDurationMinutes} min</strong></div>
          <div><small>Coach ratio</small><strong>{program.coachPlayerRatio}</strong></div>
          <div><small>Schedule</small><strong>{program.schedule || "Flexible"}</strong></div>
          <div><small>Price</small><strong>{program.priceCents > 0 ? `$${(program.priceCents / 100).toFixed(0)}` : "Contact us"}</strong></div>
        </div>

        <div className="program-detail-columns">
          {columns.map(([title, items]) => (
            <div key={title}>
              <p className="eyebrow"><span>+</span>{title}</p>
              {items?.length ? (
                <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
              ) : (
                <p className="empty">Details coming soon.</p>
              )}
            </div>
          ))}
        </div>

        <JoinProgram programId={program._id} />
      </main>
      <SiteFooter />
    </>
  );
}
